import { Vector2 } from "@babylonjs/core/Maths/math.vector";
import { AbstractRoomTool } from "./tools";
import { WallInteractors } from "./interactors";
import { Util } from "../utils/Util";
import { Wall as mdlWall, WallEnding } from "../mdl/Wall";
import { WallSnapping } from './WallSnapping';
import { CoordinatesType } from "@egr/wcf/modules/core/view/View";
import { PointerInfo, PointerButton } from "@egr/wcf/modules/core/view/InputState";
import { InsertElements } from "@egr/wcf/modules/core/cmd";
import { InteractionMode } from "@egr/wcf/modules/core/tool";
import { eq } from "@egr/wcf/modules/utils/math";
import { isPointOnSegment2, VECTOR2_X } from "@egr/wcf/modules/core/utils/MathUtils";
export class Wall extends AbstractRoomTool {
    constructor(pRoomManager, pStartPos = null, pPrevWall = null, pMoveWallMode = null) {
        super(pRoomManager);
        this.mWallSnapping = new WallSnapping(this.mRoomManager);
        this.mWall = null;
        this.mTouchMode = false;
        this.mMayCreateWall = false;
        this.mLengthFeedback = null;
        this.mStartPos = pStartPos;
        this.mPrevWall = pPrevWall;
        this.mPreWallMoveWallMode = pMoveWallMode;
        this.mGapLength = 0;
    }
    start(pParameter) {
        this.mWall = null;
        this.mView = null;
        this.mLengthFeedback = null;
        this.mTouchMode = false;
        this.mMayCreateWall = false;
        this.setInteractionMode(InteractionMode.Primary);
        this.mWallSnapping.prepareSnapping(WallSnapping.Mode.MoveStart);
        this.mApp.model.beginSelectionChange();
        let tSel = this.mRoomManager.app.model.selection;
        for (let tElement of tSel) {
            if (tElement instanceof mdlWall) {
                this.mRoomManager.app.model.removeFromSelection(tElement);
                tElement.showInteractors(false);
            }
        }
        this.mRoomManager.app.model.setSelection([]);
        this.mRoomManager.beginInteraction();
        if (this.mStartPos != null) {
            this.startWall(Util.to3d(this.mStartPos), this.mPrevWall);
        }
        return (super.start(pParameter));
    }
    stop() {
        if (this.mWall != null) {
            this.mWall.endMoving(WallSnapping.Mode.MoveWall);
            this.mWall._setConnection(WallEnding.Start, null, null);
            this.mWall.removeDummySideWalls();
            this.mWall.dispose();
            this.mWall = null;
        }
        super.stop();
        this.clearFeedback();
        this.mRoomManager.endInteraction();
        this.mApp.model.endSelectionChange();
    }
    // tslint:disable-next-line:cyclomatic-complexity
    handlePointerDown(pEvent, pState) {
        if (!pState.isPrimaryPointer(pEvent)) {
            //disable wall creation if user uses more than one finger (e.g. zoom)
            if (this.mWall == null)
                this.mMayCreateWall = false;
            return;
        }
        let tPointer = pState.pointers.get(pEvent.pointerId);
        if (tPointer == null)
            return;
        let tPos = this.getPlanePickPos2d(tPointer.position);
        if (tPos != null && (this.mPrevWall != null && this.mPreWallMoveWallMode != null)) {
            if (this.mPreWallMoveWallMode == WallSnapping.Mode.MoveEnd) {
                this.mGapLength = Vector2.Distance(Util.to2d(tPos), this.mPrevWall.endPoint);
                tPos = Util.to3d(this.mPrevWall.endPoint);
            }
            if (this.mPreWallMoveWallMode == WallSnapping.Mode.MoveStart) {
                this.mGapLength = Vector2.Distance(Util.to2d(tPos), this.mPrevWall.startPoint);
                tPos = Util.to3d(this.mPrevWall.startPoint);
            }
        }
        let tFirstDownEvent = this.mWall == null;
        if (tFirstDownEvent) {
            this.mView = this.mRoomManager.view;
            this.mTouchMode = pState.pointerType !== "mouse";
        }
        if (this.mTouchMode) {
            this.mMayCreateWall = true;
        }
        else if (tFirstDownEvent)
            this.startWall(tPos, null);
        else
            this.finishWall(false, false);
    }
    handlePointerUp(pEvent, pState) {
        let tPointer = pState.pointers.get(pEvent.pointerId);
        if (tPointer == null)
            return;
        if (pState.isPrimaryPointer(pEvent) && this.mTouchMode && this.mMayCreateWall) {
            //create default wall if touch pointer wasn't moved
            let tDist = Vector2.Distance(tPointer.initialPosition, tPointer.position);
            this.finishWall(eq(tDist, 0), true);
        }
        else {
            this.mMayCreateWall = false;
            //stop on right mouse click
            let tisMousePointer = PointerInfo.IsMousePointer(pEvent);
            let tinitialButton = tPointer.initialButton === PointerButton.Right ? true : false;
            if (tisMousePointer && tinitialButton) {
                let tDist = Vector2.Distance(tPointer.initialPosition, tPointer.position);
                if (eq(tDist, 0))
                    this.stop();
            }
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    handlePointerMove(pEvent, pState) {
        let ttmpPointer = pState.pointers.get(pEvent.pointerId);
        let tpointerOffset = new Vector2(0, 0);
        let toffset = 0;
        if (ttmpPointer != null) {
            let tinit = new Vector2(ttmpPointer.initialPosition.x, ttmpPointer.initialPosition.y);
            let tcurrent = new Vector2(ttmpPointer.position.x, ttmpPointer.position.y);
            let tdist = Vector2.Distance(tinit, tcurrent);
            tpointerOffset = (tcurrent.subtract(tinit)).normalize().scale(0);
            toffset = tdist;
        }
        if (!pState.isPrimaryPointer(pEvent) && !pState.isMouseHoverPointer(pEvent))
            return;
        let tPointerPos = null;
        if (pState.isMouseHoverPointer(pEvent)) {
            tPointerPos = this.mApp.viewer.inputState.pointerPosition;
        }
        else {
            let tPointer = pState.pointers.get(pEvent.pointerId);
            if (tPointer == null)
                return;
            tPointerPos = tPointer.position;
            if (this.mStartPos != null && this.mView != null) {
                let tcurrentPos = this.getPlanePickPos2d(tPointer.position);
                if (tcurrentPos != null) {
                    let tWallDirection = (tcurrentPos.subtract(Util.to3d(this.mStartPos))).normalizeToNew();
                    let tnewPos = tcurrentPos.subtract(tWallDirection.scale(0)); // with offset: this.mGapLength
                    let ttmpPointerPos = this.mView.projectPoint2(tnewPos, CoordinatesType.Client, null);
                    if (ttmpPointerPos != null)
                        tPointerPos = ttmpPointerPos;
                }
            }
            //start wall creation in touch mode
            if (tPointer != null && this.mWall == null && this.mTouchMode && this.mMayCreateWall) {
                let tDist = Vector2.Distance(tPointer.initialPosition, tPointer.position);
                if (tDist > 10) {
                    let tPos1 = this.getPlanePickPos2d(tPointer.initialPosition);
                    if (this.mStartPos != null)
                        tPos1 = Util.to3d(this.mStartPos);
                    this.startWall(tPos1, null);
                }
                else {
                    tPointerPos = tPointer.position;
                }
            }
        }
        let tPos = this.getPlanePickPos2d(tPointerPos);
        if (tPos != null) {
            if (!this.mRoomManager._deviceHasMouse()) {
                if (toffset > 10) {
                    if (this.mStartPos == null) {
                        let tnewPos = tPointerPos.subtract(tpointerOffset);
                        tPos = this.getPlanePickPos2d(tnewPos);
                        if (tPos != null)
                            this.updatePointerPosition(tPos);
                    }
                    else {
                        this.updatePointerPosition(tPos);
                    }
                }
                else
                    this.updatePointerPosition(tPos);
            }
            else
                this.updatePointerPosition(tPos);
            this.updateFeedback();
        }
    }
    prepareFeedback() {
        if (this.mWall != null) {
            this.mLengthFeedback = new WallInteractors(this.mRoomManager, this.mWall);
            this.mLengthFeedback._updateLengthInteractors();
        }
    }
    clearFeedback() {
        if (this.mLengthFeedback != null) {
            this.mLengthFeedback.clear();
            this.mLengthFeedback = null;
        }
        this.mWallSnapping.clear();
    }
    updateFeedback() {
        if (this.mLengthFeedback != null) {
            this.mLengthFeedback._updateLengthInteractors();
        }
    }
    updatePointerPosition(pPos) {
        if (this.mWall != null) {
            let tSnapResult = this.mWallSnapping.snap(pPos, this.mView);
            let tnewPos = tSnapResult.vector_Point;
            if (this.mRoomManager.shiftPressed) {
                let tStart = Util.to3d(this.mWall.startPoint);
                tnewPos = Util.calcSnapPoint(tStart, tnewPos, 45);
            }
            this.mWall.setEndPoint(Util.to2d(tnewPos));
            this.mWall._findNewConnection(0);
        }
        else {
            this.mWallSnapping.snap(pPos, this.mRoomManager.hoverView);
        }
        this.mApp.viewer.requestRenderFrame();
    }
    startWall(pPos, pPrevWall) {
        if (pPos == null || this.mView == null)
            return;
        let tSnapResult = this.mWallSnapping.snap(pPos, this.mView);
        this.mWall = new mdlWall(this.mRoomManager, true);
        this.mWall.setDrawingNew(true);
        this.mWall.setStartPoint(new Vector2(tSnapResult.vector_Point.x, tSnapResult.vector_Point.z));
        this.mWall.setEndPoint(new Vector2(tSnapResult.vector_Point.x + 0.21, tSnapResult.vector_Point.z));
        this.mWall.beginMoving(WallSnapping.Mode.MoveEnd, this.mWall.endPoint);
        this.mWallSnapping.prepareSnapping(WallSnapping.Mode.MoveStart, this.mWall, WallEnding.Start);
        if (pPrevWall != null) {
            this.mWall.setThickness(pPrevWall.thickness);
            this.mWall.setHeight(pPrevWall.height);
            pPrevWall._setConnection(WallEnding.End, this.mWall, WallEnding.Start);
        }
        else {
            //find potential connection wall and use height and thickness
            let tNearestWall = this.mRoomManager.getNearestWall(this.mWall.startPoint, this.mWall);
            if (tNearestWall != null && isPointOnSegment2(this.mWall.startPoint, tNearestWall.startPoint, tNearestWall.endPoint)) {
                this.mWall.setThickness(tNearestWall.thickness);
                this.mWall.setHeight(tNearestWall.height);
            }
        }
        this.prepareFeedback();
        this.mApp.viewer.requestRenderFrame();
    }
    finishWall(pCreateDefaultWall, pTouchMode) {
        if (this.mWall == null || (this.mWall !== null && this.mWall.getWallLengthBase() <= 0.2)) {
            this.stop();
            return;
        }
        this.mWall.setDrawingNew(false);
        let tNewWall = this.mWall;
        this.mWall = null;
        tNewWall.endMoving(WallSnapping.Mode.MoveWall);
        this.clearFeedback();
        if (pCreateDefaultWall) {
            tNewWall.setEndPoint(tNewWall.startPoint.add(VECTOR2_X));
        }
        let tCmd = new InsertElements(this.mApp, [tNewWall]);
        this.mApp.commands.executeCommand(tCmd).then(() => {
            //setup connection
            if (!pCreateDefaultWall) {
                tNewWall._findNewConnection(WallEnding.Start);
                tNewWall._findNewConnection(WallEnding.End);
                this.mApp.rendering.notifyModelInteraction();
            }
            let tStartCnx = tNewWall.startCxn;
            let tEndCnx = tNewWall.endCxn;
            if (tStartCnx.wall != null) {
                tStartCnx.wall._cutEdges();
            }
            if (tEndCnx.wall != null) {
                tEndCnx.wall._cutEdges();
            }
            tNewWall.createDummySideWalls();
            this.mApp.model.setSelection([tNewWall]);
            if (pCreateDefaultWall || pTouchMode || tNewWall.endCxn.wall != null) {
                this.stop();
                //this.mRoomManager.findRoom();
            }
            else {
                this.startWall(Util.to3d(tNewWall.endPoint), tNewWall);
            }
        });
        this.mApp.viewer.requestRenderFrame();
    }
}
